import { computed, ref } from 'vue'

export function useTimer() {
  const intervalId = ref<NodeJS.Timeout | undefined>(undefined)

  function start(callback: () => void, interval: number) {
    intervalId.value = setInterval(callback, interval)
  }

  function stop() {
    clearInterval(intervalId.value)
    intervalId.value = undefined
  }

  const isRunning = computed(() => !!intervalId.value)

  return {
    start,
    stop,
    isRunning,
  }
}

type PaginationOptions = {
  itemsPerPage: number
  page: number
  total: number
  lastPage: number
  itemsPerPageOptions: number[]
}

export type PaginationResponse = {
  currentPage: number
  from: number
  lastPage: number
  path: string
  perPage: number
  to: number
  total: number
}

export const usePagination = (options?: Partial<PaginationOptions>) => {
  const itemsPerPage = ref(options?.itemsPerPage || 15)
  const page = ref(options?.page || 1)
  const total = ref(options?.total || 0)
  const lastPage = ref(options?.lastPage || 1)
  const itemsPerPageOptions = ref(options?.itemsPerPageOptions || [15, 30, 50, 100])

  function setNextPage() {
    if (page.value < lastPage.value) page.value++
    return page.value
  }

  function setPreviousPage() {
    if (page.value > 1) page.value--
    return page.value
  }

  function setLastPage() {
    return (page.value = lastPage.value)
  }

  function setFirstPage() {
    return (page.value = 1)
  }

  function setFromResponse(response: PaginationResponse) {
    page.value = response.currentPage
    lastPage.value = response.lastPage
    total.value = response.total
  }

  const hasNextPage = computed(() => page.value !== lastPage.value)

  return {
    itemsPerPage,
    page,
    total,
    lastPage,
    itemsPerPageOptions,
    hasNextPage,
    setNextPage,
    setPreviousPage,
    setLastPage,
    setFirstPage,
    setFromResponse,
  }
}
